import React, { useState, useMemo } from 'react'
import { Button, Spin, Icon, message, Modal } from 'antd'
import ModeCard from './components/ModeCard'
import AudioModeSelector from './components/AudioModeSelector'
import './ModeSelectionPage.scss'

const MODE_OPTIONS = [
  {
    key: 'manual',
    icon: 'ordered-list',
    title: 'Manual Mode',
    description:
      'Create your video step-by-step. Full control over every scene, precise edits, and detailed customization as you go.',
    color: '#1890ff'
  },
  {
    key: 'draft',
    icon: 'rocket',
    title: 'Draft Mode',
    description:
      'Fast-track your setup. Configure everything on one screen while AI generates shots in the background.',
    color: '#52c41a'
  }
]

const ModeSelectionPage = ({
  onSelectMode,
  onOverrideMode,
  onCloneMode,
  isSavingVideoData,
  isCloning,
  videoId,
  onGoBack,
  initialMode,
  initialAudioMode,
  shotList
}) => {
  const [selectedMode, setSelectedMode] = useState(initialMode || null)
  const [selectedAudioMode, setSelectedAudioMode] = useState(
    initialAudioMode || 'narration'
  )
  const [switchModalVisible, setSwitchModalVisible] = useState(false)
  const [switchContext, setSwitchContext] = useState(null) // { mode, audioMode, type }
  const isPreparingWorkspace = !videoId

  const hasGeneratedContent = useMemo(() => {
    if (!shotList || shotList.length === 0) return false
    return shotList.some(scene => {
      const sceneAudio = !!scene.audio_link
      const shotHasContent =
        scene.shots &&
        scene.shots.some(shot => {
          return !!shot.imageSrc || !!shot.vidSrc || !!shot.audio_link
        })
      return sceneAudio || shotHasContent
    })
  }, [shotList])

  const handleModeClick = modeKey => {
    if (isPreparingWorkspace) return

    if (modeKey !== selectedMode && initialMode && hasGeneratedContent) {
      setSwitchContext({
        mode: modeKey,
        audioMode: selectedAudioMode,
        type: 'video_mode'
      })
      setSwitchModalVisible(true)
      return
    }
    setSelectedMode(modeKey)
  }

  const handleAudioModeClick = modeKey => {
    if (isPreparingWorkspace) return

    if (
      modeKey !== selectedAudioMode &&
      initialAudioMode &&
      hasGeneratedContent &&
      modeKey !== 'dialogue'
    ) {
      setSwitchContext({
        mode: selectedMode,
        audioMode: modeKey,
        type: 'audio_mode'
      })
      setSwitchModalVisible(true)
      return
    }
    setSelectedAudioMode(modeKey)
  }

  const handleModalAction = actionType => {
    if (!switchContext) return

    if (actionType === 'clone') {
      onCloneMode(
        switchContext.mode,
        switchContext.audioMode,
        switchContext.type
      )
    } else if (actionType === 'override') {
      onOverrideMode(
        switchContext.mode,
        switchContext.audioMode,
        switchContext.type
      )
      if (switchContext.type === 'video_mode') {
        setSelectedMode(switchContext.mode)
      } else {
        setSelectedAudioMode(switchContext.audioMode)
      }
    }

    setSwitchModalVisible(false)
    setSwitchContext(null)
  }

  const showPreparingLoading =
    isPreparingWorkspace || (isSavingVideoData && !videoId)

  const handleContinue = () => {
    if (isPreparingWorkspace) {
      message.info('Preparing video workspace. Please wait a moment.')
      return
    }

    if (!selectedMode) {
      message.warning('Please select a creation mode to continue.')
      return
    }

    onSelectMode(selectedMode, selectedAudioMode)
  }

  const canContinue = !!selectedMode && !isPreparingWorkspace

  return (
    <div className="mode-selection-page">
      <Spin spinning={showPreparingLoading} tip="Preparing video workspace...">
        <div className="mode-selection-page__container">
          {/* Header */}
          <div className="mode-selection-page__header">
            <div className="mode-selection-page__header-text">
              <div className="mode-selection-page__badge">
                <Icon type="setting" />
                <span>Setup</span>
              </div>
              <h1 className="mode-selection-page__title">
                Choose Your Creation Mode
              </h1>
              <p className="mode-selection-page__subtitle">
                Select how you'd like to create your video and configure the
                audio style for your story.
              </p>
            </div>
          </div>

          {/* Mode Selection Cards */}
          <div className="mode-selection-page__section">
            <div className="mode-selection-page__section-label">
              <Icon type="video-camera" />
              <span>Video Creation Mode</span>
            </div>
            <div className="mode-selection-page__cards">
              {MODE_OPTIONS.map(mode => (
                <ModeCard
                  key={mode.key}
                  icon={mode.icon}
                  title={mode.title}
                  description={mode.description}
                  color={mode.color}
                  selected={selectedMode === mode.key}
                  onClick={() => handleModeClick(mode.key)}
                  disabled={isPreparingWorkspace}
                />
              ))}
            </div>
          </div>

          {/* Audio Mode Selection */}
          <div className="mode-selection-page__section">
            <AudioModeSelector
              selectedMode={selectedAudioMode}
              onSelectMode={handleAudioModeClick}
              disabled={isPreparingWorkspace}
            />
          </div>

          {/* Continue Button */}
          <div className="mode-selection-page__footer">
            {onGoBack && (
              <Button
                size="large"
                onClick={onGoBack}
                className="mode-selection-page__back-btn"
              >
                <Icon type="left" />
                Story
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              disabled={!canContinue}
              loading={showPreparingLoading}
              onClick={handleContinue}
              className="mode-selection-page__continue-btn"
            >
              Continue
              <Icon type="right" />
            </Button>
          </div>
        </div>
      </Spin>

      <Modal
        maskClosable={false}
        visible={switchModalVisible}
        onCancel={() => setSwitchModalVisible(false)}
        footer={null}
        centered
        width={520}
        className="video-style-change-modal"
      >
        <div className="video-style-change-modal-content">
          <div className="modal-icon-wrapper">
            <Icon type="warning" className="modal-warning-icon" />
          </div>
          <h3 className="modal-title">
            {switchContext?.type === 'video_mode'
              ? 'Switch Creation Mode'
              : 'Switch Audio Mode'}
          </h3>
          <p className="modal-description">
            {switchContext?.type === 'video_mode'
              ? 'Switching the creation mode will require clearing existing generated content (images, videos, and audios) to maintain consistency.'
              : 'Switching the audio mode after content generation will clear the entire shotlist and its associated assets to ensure a fresh start with the new audio style.'}
            <br />
            <br />
            Please choose how you would like to proceed:
          </p>

          <div className="modal-options">
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="copy" />
              </div>
              <div className="option-content">
                <h4 className="option-title">
                  Clone as New Video (Recommended)
                </h4>
                <p className="option-description">
                  Safely creates a brand new copy of this video with the new
                  settings. Your current video and its assets will be saved and
                  remain untouched.
                </p>
              </div>
            </div>
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="edit" />
              </div>
              <div className="option-content">
                <h4 className="option-title">Override Current Video</h4>
                <div className="option-description">
                  Updates this exact video.
                  <div
                    style={{
                      border: '1px solid #ffa39e',
                      backgroundColor: '#fff1f0',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      marginTop: '8px',
                      fontSize: '13px',
                      color: 'rgba(0, 0, 0, 0.65)'
                    }}
                  >
                    <Icon
                      type="warning"
                      theme="filled"
                      style={{ color: '#f5222d', marginRight: '6px' }}
                    />
                    <strong>Warning:</strong> All your existing generated
                    content will be deleted. This cannot be undone.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <Button onClick={() => setSwitchModalVisible(false)} size="large">
              Cancel
            </Button>

            <div className="modal-actions-right">
              <Button
                type="default"
                onClick={() => handleModalAction('override')}
                size="large"
                icon="edit"
                className="override-btn"
              >
                Override
              </Button>
              <Button
                type="primary"
                onClick={() => handleModalAction('clone')}
                size="large"
                icon="copy"
                loading={isCloning}
              >
                Clone & Switch
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ModeSelectionPage
